import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import clsx from 'clsx';
import type { Feature } from '@/types/content';

interface FeatureShowcaseProps {
  features: Feature[];
  className?: string;
}

export function FeatureShowcase({ features, className }: FeatureShowcaseProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeFeature = features[activeIndex];

  if (!activeFeature) return null;

  return (
    <div className={clsx('', className)}>
      {/* Tab navigation */}
      <div className="flex flex-wrap gap-2 mb-8 justify-center">
        {features.map((feature, index) => (
          <button
            key={feature.id}
            onClick={() => setActiveIndex(index)}
            className={clsx(
              'px-5 py-2.5 rounded-full text-sm font-medium transition-all',
              index === activeIndex
                ? 'bg-accent text-white'
                : 'bg-cream text-ink hover:bg-gray-200'
            )}
          >
            {feature.name}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        {/* Text content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <h3 className="font-display text-h3 mb-3">{activeFeature.name}</h3>
            <p className="text-accent font-medium mb-4">{activeFeature.tagline}</p>
            <p className="text-muted mb-6">{activeFeature.description}</p>

            <ul className="space-y-3">
              {activeFeature.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckIcon className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>

            <a
              href={`/features#${activeFeature.id}`}
              className="inline-flex items-center gap-2 mt-6 text-accent font-medium hover:underline"
            >
              Learn more about {activeFeature.name}
              <ArrowRightIcon className="w-4 h-4" />
            </a>
          </motion.div>
        </AnimatePresence>

        {/* Screenshot/image */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeFeature.id + '-image'}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="aspect-[4/3] rounded-2xl bg-cream overflow-hidden shadow-elevated">
              {activeFeature.screenshotUrl ? (
                <img
                  src={activeFeature.screenshotUrl}
                  alt={`${activeFeature.name} screenshot`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center text-muted">
                    <FeatureIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">{activeFeature.name}</p>
                  </div>
                </div>
              )}
            </div>
            {/* Decorative element */}
            <div className="absolute -z-10 -bottom-4 -right-4 w-full h-full rounded-2xl bg-accent/10" />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// Icons
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function FeatureIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25" />
    </svg>
  );
}
